import * as actionTypes from '@/store/actions.type';
import * as mutationTypes from '@/store/mutations.type';
import { 
  DEVICE_STATUS_TYPES,
  DEVICE_FETCH_PARAMS
} from '@/constants/device.js';
import api from '@/api';

const getDefaultSingleDevice = () => ({
  name: '',
  imei: '',
  refreshRate: null,
  simPhoneNumber: '',
  type: null,
  deviceStatus: DEVICE_STATUS_TYPES.available
})

const getDefaultFilter = () => ({
  name: '',
  imei: '',
  deviceType: null,
  deviceStatusType: null,
  sortDevicesBy: null,
  sortDirection: null,
  skip: 0,
  take: 10
})

const deviceMapper = ({
  id,
  name,
  imei,
  refreshRate,
  simPhoneNumber,
  type,
  deviceStatus,
  externalDeviceId
}) => ({
  id,
  name,
  imei,
  refreshRate,
  simPhoneNumber,
  type,
  deviceStatus,
  externalDeviceId
})

const state = {
  list: [],
  singleDevice: getDefaultSingleDevice(),
  loading: false,
  loadingNewRefreshRate: false,
  filter: getDefaultFilter(),
  devicesCount: 0,
  singleDeviceDetails: getDefaultSingleDevice(),
  locations: [],
  move: null,
  moveLoading: false,
  devicesUsage: [],
  locationsLoading: false
}

const getters = {
  devices: state => state.list,
  singleDevice: state => state.singleDevice,
  loadingDevices: state => state.loading,
  deviceListFilter: state => state.filter,
  devicesCount: state => state.devicesCount,
  loadingNewRefreshRate: state => state.loadingNewRefreshRate,
  deviceLocations: state => state.locations,
  deviceMove: state => state.move,
  deviceMoveLoading: state => state.moveLoading,
  devicesUsage: state => state.devicesUsage,
  deviceLocationsLoading: state => state.locationsLoading
}

const actions = {
  async [actionTypes.CREATE_DEVICE] ({ commit }, params) {
    try {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, true);
      await api.devices.create(params);

      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);
    } catch(error) {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);

      throw error;
    }
  },
  async [actionTypes.UPDATE_DEVICE] ({ commit }, params) {
    try {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, true);

      await api.devices.updateOne(params);

      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);
    } catch(error) {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);

      throw error;
    }
  },
  async [actionTypes.FETCH_SINGLE_DEVICE] ({ commit, state }, params) {
    try {
      const cachedDevices = state.list.filter(d => (d.id === params.deviceId));
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, true);

      if(cachedDevices && cachedDevices.length && !params.loadAdditionalData) {
        const data = deviceMapper(cachedDevices[0]);

        commit(mutationTypes.UPDATE_SINGLE_DEVICE, data);
      } else {
        const { data } = await api.devices.getOne(params);
        let device;

        if(params.loadAdditionalData) {
          device = data;
        } else {
          device = deviceMapper(data);
        }
        
        commit(mutationTypes.UPDATE_DEVICE_LOCATIONS, data.deviceLocations);
        commit(mutationTypes.UPDATE_SINGLE_DEVICE, device);
      }

      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);
    } catch (error) {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);
      
      throw error;
    }
  },
  async [actionTypes.UPDATE_DEVICES_FILTER] ({ commit, state }, params) {
    try {
      let newFilter;

      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, true);

      if(params) {
        newFilter = {
          name: params.name || '',
          imei: params.imei || '',
          deviceType: (params.deviceType || params.deviceType === 0) ? params.deviceType : null,
          deviceStatusType: (params.deviceStatusType || params.deviceStatusType === 0) ? params.deviceStatusType : null,
          sortDevicesBy: (params.sortBy && params.sortBy.length) ? DEVICE_FETCH_PARAMS[params.sortBy[0]] : null,
          sortDirection: (params.sortDesc && params.sortDesc.length) ? +params.sortDesc[0] : null,
          skip: function() {
            if(!params.page || !params.itemsPerPage) {
              return params.skip;
            }
    
            return (params.page * params.itemsPerPage) - params.itemsPerPage;
          }(),
          take: params.itemsPerPage || 10
        }

        commit(mutationTypes.UPDATE_DEVICES_FILTER, params);

      } else {
        newFilter = { ...getDefaultFilter(), ...{ take: state.filter.take } };
        commit(mutationTypes.UPDATE_DEVICES_FILTER, newFilter);
      }

  
      const { data } = await api.devices.query(newFilter);
  
      commit(mutationTypes.UPDATE_LIST_OF_DEVICES, { data: data.devices });
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);
      commit(mutationTypes.UPDATE_DEVICE_COUNT, data.devicesCount);
    } catch (error) {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);

      throw error;
    }
  },
  async [actionTypes.DELETE_DEVICE] ({ commit, state }, deviceId) {
    try {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, true);

      await api.devices.deleteOne(deviceId);

      const { data } = await api.devices.query(state.filter);
  
      commit(mutationTypes.UPDATE_LIST_OF_DEVICES, { data: data.devices });
      commit(mutationTypes.UPDATE_DEVICE_COUNT, data.devicesCount);
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);

    } catch (error) {
      commit(mutationTypes.UPDATE_LOADING_DEVICE_STATE, false);

      throw error;
    }
  },
  [actionTypes.RESET_SINGLE_DEVICE_STATE] ({ commit }) {
    commit(mutationTypes.RESET_SINGLE_DEVICE_STATE);
    commit(mutationTypes.UPDATE_DEVICE_MOVE_DETAILS, null);
  },
  async [actionTypes.UPDATE_REFRESH_RATE] ({ commit, state }, refreshRate) {
    try {
      commit(mutationTypes.UPDATE_LOADING_REFRESH_RATE, true);

      const { data } = await api.devices.updateRefreshRate(state.singleDevice.id, refreshRate);

      const device = data
      
      commit(mutationTypes.UPDATE_SINGLE_DEVICE, device);
      commit(mutationTypes.UPDATE_LOADING_REFRESH_RATE, false);

    } catch (error) {
      commit(mutationTypes.UPDATE_LOADING_REFRESH_RATE, false);

      throw error;
    }
  },
  async [actionTypes.FETCH_DEVICE_LOCATIONS] ({ commit, state, dispatch }, params) {
    try {
      commit(mutationTypes.SET_LOCATIONS_LOADING, true);

      const requestParams = {
        deviceId: state.singleDevice.id,
        loadAdditionalData: true,
        ...params
      };

      const { data } = await api.devices.getOne(requestParams);

      commit(mutationTypes.UPDATE_DEVICE_LOCATIONS, data.deviceLocations);

      commit(mutationTypes.SET_LOCATIONS_LOADING, false);

      dispatch(actionTypes.FETCH_DEVICE_MOVE_DETAILS, params.moveId);
      
    } catch (error) {
      commit(mutationTypes.SET_LOCATIONS_LOADING, false);
      throw error;
    }
  },
  async [actionTypes.FETCH_DEVICE_MOVE_DETAILS] ({ commit }, params) {
    try {
      if(params) {
        commit(mutationTypes.SET_MOVE_LOADING, true);
        const { data } = await api.moves.getOne(params);

        commit(mutationTypes.UPDATE_DEVICE_MOVE_DETAILS, data);
        commit(mutationTypes.SET_MOVE_LOADING, false);
      } else {
        commit(mutationTypes.UPDATE_DEVICE_MOVE_DETAILS, null);
      }

    } catch (error) {
      commit(mutationTypes.SET_MOVE_LOADING, false);

      throw error;
    }
  },
  async [actionTypes.FETCH_DEVICES_USAGE] ({ commit }, payload) {
    const { data } = await api.devices.getDeviceUsage(payload);

    commit(mutationTypes.UPDATE_DEVICES_USAGE, data);
  },
  [actionTypes.UPDATE_DEFAULT_DEVICE_REFRESH_RATE] ({ commit }, payload) {
    if(payload) {
      commit(mutationTypes.UPDATE_DEFAULT_DEVICE_REFRESH_RATE, payload.defaultDeviceRefreshRate)
    }
  },
  [actionTypes.RESET_DEVICE_FILTER] ({ commit }) {
    commit(mutationTypes.RESET_DEVICE_FILTER);
  }
}

const mutations = {
  [mutationTypes.UPDATE_LIST_OF_DEVICES] (state, { data, findAndModify }) {
    if(data.push) {
      state.list = data;
    } else {
      if(findAndModify) {
        state.list.forEach((device, i) => {
          if(device.id === data.id) {
            state.list[i] = data;
            return;
          }
        })
      } else {
        state.list.push(data);
      }
    }
  },
  [mutationTypes.UPDATE_SINGLE_DEVICE] (state, payload) {
    state.singleDevice = payload;
  },
  [mutationTypes.RESET_SINGLE_DEVICE_STATE] (state) {
    state.singleDevice = getDefaultSingleDevice();
  },
  [mutationTypes.UPDATE_LOADING_DEVICE_STATE] (state, payload) {
    state.loading = payload;
  },
  [mutationTypes.UPDATE_DEVICE_COUNT] (state, payload) {
    state.devicesCount = payload;
  },
  [mutationTypes.UPDATE_DEVICES_FILTER] (state, payload) {
    state.filter = payload;
  },
  [mutationTypes.UPDATE_LOADING_REFRESH_RATE] (state, payload) {
    state.loadingNewRefreshRate = payload;
  },
  [mutationTypes.UPDATE_DEVICE_LOCATIONS] (state, payload) {
    state.locations = payload;
  },
  [mutationTypes.UPDATE_DEVICE_MOVE_DETAILS] (state, payload) {
    state.move = payload;
  },
  [mutationTypes.SET_MOVE_LOADING] (state, payload) {
    state.moveLoading = payload;
  },
  [mutationTypes.UPDATE_DEVICES_USAGE] (state, payload) {
    state.devicesUsage = payload;
  },
  [mutationTypes.SET_LOCATIONS_LOADING] (state, payload) {
    state.locationsLoading = payload;
  },
  [mutationTypes.UPDATE_DEFAULT_DEVICE_REFRESH_RATE] (state, payload) {
    state.singleDevice.refreshRate = payload;
  },
  [mutationTypes.RESET_DEVICE_FILTER] (state) {
    state.filter = getDefaultFilter();
  }
}

export default {
  state,
  actions,
  mutations,
  getters
}